import {
  buffers, type Channel,
} from 'redux-saga';
import {
  actionChannel,
  all,
  call, put, take,
  takeEvery,
} from 'redux-saga/effects';
import { SelectedDataType } from '~/_shared/components/customizeMetrics/selectedDataMetric.type';
import { CONFIG } from '~/_shared/constants/config';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import type { MetricModel } from '~/_shared/utils/metric/metrics.factory';
import { select } from '~/_shared/utils/saga/effects';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { createBoundaryTerritoryGroupRequestParams } from '~/sidebar/sidebarApps/mapTools/boundary/boundaryTerritoryGroup.helpers';
import {
  createDataLayerDemographicBTGRequestParams,
  isDataLayersArtificialBoundaryTerritoryGroup,
} from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.helpers';
import { boundaryGroupSelector } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { waitForBoundaryGroups } from '~/store/boundaryItems/boundaryItems.sagas';
import {
  BoundaryTerritoryGroupConnectedEntities, BoundaryTerritoryType,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { boundaryTerritoryGroupsRemoveCompleted } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { createOfflineBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.sagas';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { type boundaryToolBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import { dataLayersBTGsSelector } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersBTGs.selectors';
import { isBTGForDataLayerDemographic } from '~/store/boundaryTerritoryGroups/dataLayersTool/dataLayersToolBTGs.helpers';
import { waitForPremiumDemographicsReady } from '~/store/demographics/premiumDemographics/demographicsListPremium.sagas';
import {
  activePremiumDemographicsAllSelector,
  demographicsListPremiumActiveSelector,
} from '~/store/demographics/premiumDemographics/demographicsListPremium.selectors';
import { mapToolChanged } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import {
  dataLayersToolBoundaryTerritoryGroupsCreated, dataLayersToolUpdateFinished, dataLayersToolUpdateRequest,
  dataLayersToolUpdateStarted,
} from '~/store/frontendState/mapTools/dataLayers/dataLayers.actionCreators';
import {
  mapSettingsDataLayersAddDataLayer, mapSettingsDataLayersRemoveDataLayer, mapSettingsDataLayersUpdateBoundaryGroup, mapSettingsDataLayersUpdateDataLayer,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  mapSettingsActiveDataLayerGroupSelector,
  mapSettingsDataLayersIndividualLayerSettingsSelector,
  mapSettingsDataLayersPrimaryBoundaryGroupIdSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import { waitForMapSettingsReady } from '~/store/mapSettings/ready/mapSettingsReady.sagas.helpers';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';

export function* mapSettingsDataLayersSaga() {
  if (CONFIG.ENABLE_DATA_LAYERS) {
    yield takeEvery([
      mapSettingsDataLayersAddDataLayer,
      mapSettingsDataLayersRemoveDataLayer,
      mapSettingsDataLayersUpdateDataLayer,
      mapSettingsDataLayersUpdateBoundaryGroup,
    ], onDataLayersChanged);
    yield takeEvery(mapToolChanged, onMapToolChanged);
    yield all([
      watchForDataLayersUpdateRequests(),
    ]);
  }
}

function* onDataLayersChanged() {
  yield put(dataLayersToolUpdateRequest());
}

function* onMapToolChanged(action: ReturnType<typeof mapToolChanged>) {
  const openedTool = action.payload.newTool;

  if (openedTool === MapTool.DataLayers) {
    yield call(waitForMapSettingsReady);
    yield call(waitForPremiumDemographicsReady);
    yield call(waitForBoundaryGroups, { waitForBTGs: false });
    yield put(dataLayersToolUpdateRequest());
  }
}

function* watchForDataLayersUpdateRequests() {
  const channel: Channel<Action> = yield actionChannel(dataLayersToolUpdateRequest, buffers.sliding(1));

  while (true) {
    yield take(channel);
    try {
      yield call(updateDataLayerBoundaryGroups);
    }
    catch (error) {
      console.error('Error updating Data Layer boundary groups', error);
    }
  }
}

function* updateDataLayerBoundaryGroups() {
  yield put(dataLayersToolUpdateStarted());

  const clientId: ReturnType<typeof clientIdSelector> = yield select(clientIdSelector);
  const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
  const currentDataLayersBTGs: ReturnType<typeof boundaryToolBTGsSelector> = yield select(dataLayersBTGsSelector);
  const boundaryGroupId: ReturnType<typeof mapSettingsDataLayersPrimaryBoundaryGroupIdSelector> = yield select(mapSettingsDataLayersPrimaryBoundaryGroupIdSelector);
  const boundaryGroup: ReturnType<typeof boundaryGroupSelector> = yield select(boundaryGroupSelector, boundaryGroupId);
  const activeDataLayerGroup: ReturnType<typeof mapSettingsActiveDataLayerGroupSelector> = yield select(mapSettingsActiveDataLayerGroupSelector);
  const activePremiumDemographicsAll: ReturnType<typeof activePremiumDemographicsAllSelector> = yield select(activePremiumDemographicsAllSelector);
  const individualLayerSettings: ReturnType<typeof mapSettingsDataLayersIndividualLayerSettingsSelector> = yield select(mapSettingsDataLayersIndividualLayerSettingsSelector);
  const accessibleDemographics = activePremiumDemographicsAll.accessible;

  const activeDataLayerDemographics = activeDataLayerGroup?.data.demographics ?? [];
  const allDataLayersBTGs = currentDataLayersBTGs.filter(btg => isDataLayersArtificialBoundaryTerritoryGroup(btg));

  // the following section removes BTGs created for the Data Layers Tool that are no longer needed
  const currentDataLayersBTGsToRemove = !activeDataLayerGroup ? allDataLayersBTGs : allDataLayersBTGs.filter(
    btg => !activeDataLayerDemographics.find(demographic => isBTGForDataLayerDemographic({
      dataLayerGroupId: activeDataLayerGroup.id,
      boundaryTerritoryGroup: btg,
      demographic,
    })));
  yield put(boundaryTerritoryGroupsRemoveCompleted(currentDataLayersBTGsToRemove));

  if (!mapId || !clientId || !boundaryGroup || !activeDataLayerGroup) {
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  const dataLayerBoundaryGroupCommonSettings = {
    style: activeDataLayerGroup.settings.style,
    numberOfRanges: activeDataLayerGroup.settings.numberOfRanges,
    disableMapInteraction: activeDataLayerGroup.settings.disableMapInteraction,
    hideLabels: boundaryGroup.hideLabels || activeDataLayerGroup.settings.hideLabels,
    labelStyle: activeDataLayerGroup.settings.labelStyle,
    labelValueConfig: activeDataLayerGroup.settings.labelValueConfig,
  };

  const demographicsToLoad = activeDataLayerDemographics.filter(demographic =>
    accessibleDemographics.has(demographic)
  );

  const allDemographicsAreRestricted = demographicsToLoad.length === 0;

  // if there are non-accessible demographics, we create a placeholder one
  if (allDemographicsAreRestricted) {
    const existingPlaceholderBTG = allDataLayersBTGs.find(
      btg => btg.settings.boundaryTerritoryType === BoundaryTerritoryType.Manual
    );

    yield call(
      createOfflineBoundaryTerritoryGroup,
      [{
        boundaryTerritoryGroupId: existingPlaceholderBTG?.boundaryTerritoryGroupId,
        ...createBoundaryTerritoryGroupRequestParams({
          ...dataLayerBoundaryGroupCommonSettings,
          mapId,
          boundaryGroupId: boundaryGroup.id,
          territoryType: BoundaryTerritoryType.Manual,
          connectedEntityData: {
            entity: BoundaryTerritoryGroupConnectedEntities.DataLayer,
            id: activeDataLayerGroup.id,
          },
        }),
      }],
      clientId,
    );
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  if (!demographicsToLoad || demographicsToLoad.length === 0) {
    yield put(dataLayersToolUpdateFinished());
    return;
  }

  const activeDemographics: ReturnType<typeof demographicsListPremiumActiveSelector> = yield select(demographicsListPremiumActiveSelector);
  const metrics: ReadonlyArray<MetricModel> = activeDemographics.accessible
    .filter(d => demographicsToLoad.includes(d.id))
    .map(d => ({
      type: SelectedDataType.Demographic,
      demographic: d,
    }));

  const requests = demographicsToLoad.map((demographic) => { // Reverse the order to preserve it, artificial BTGs have negative IDs (higher is older)
    // demographic-specific overrides (e.g. custom ranges/style set for this demographic only) take precedence over the data layer's shared settings
    const individualSettings = individualLayerSettings[boundaryGroup.id]?.[demographic];
    const btgSettingsOverride = individualSettings?.individualSettingsActive ? individualSettings.boundarySettings : undefined;

    return createDataLayerDemographicBTGRequestParams({
      boundaryTerritoryGroupId: allDataLayersBTGs.find(
        boundaryTerritoryGroup => isBTGForDataLayerDemographic({ dataLayerGroupId: activeDataLayerGroup.id, demographic, boundaryTerritoryGroup })
      )?.boundaryTerritoryGroupId,
      mapId,
      dataLayerGroupId: activeDataLayerGroup.id,
      boundaryGroupId: boundaryGroup.id,
      demographic,
      metrics,
      ...dataLayerBoundaryGroupCommonSettings,
      numberOfRanges: btgSettingsOverride?.numberOfRanges ?? activeDataLayerGroup.settings.numberOfRanges,
      style: {
        ...activeDataLayerGroup.settings.style,
        // Only gradient colors are overridden by the individual BTG settings
        gradientColors: btgSettingsOverride?.style?.gradientColors ?? activeDataLayerGroup.settings.style.gradientColors,
      },
      boundaryTerritories: btgSettingsOverride?.boundaryTerritories,
    });
  });

  const newBoundaryTerritoryGroups: ReadonlyArray<BoundaryTerritoryGroup> = yield call(
    createOfflineBoundaryTerritoryGroup,
    requests,
    clientId,
  );

  if (newBoundaryTerritoryGroups) {
    const finalBTGs = demographicsToLoad.map((demographicId) => {
      return newBoundaryTerritoryGroups.find(btg => btg.boundaryGroupId === boundaryGroup.id && btg.settings.demographicId === demographicId);
    }).filter(notNullOrUndefined);

    yield put(dataLayersToolBoundaryTerritoryGroupsCreated(finalBTGs));
  }
}
